import PropTypes from "prop-types";
import React, { useContext, useEffect } from "react";

import {
  ApplicationContext,
  commonPlacementComponents as placementComponents,
  useAppSetting,
  usePageId,
} from "@swa-ui/application";
import { AuthContext, useUserInfo } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useGeolocation } from "@swa-ui/geolocation";
import { isEmpty } from "@swa-ui/object";
import { usePersonas } from "@swa-ui/personas";
import { PlacementsProvider, PlacementsVisibilityProvider } from "@swa-ui/placements";

import { LocaleContext } from "../LocaleProvider";

/**
 * Dotcom wrapper around PlacementsProvider
 */

const DEFAULT_GLOBAL_PLACEMENT_IDS = [
  "footer",
  "globalNavFooter",
  "globalNavHeader",
  "messageIncidentAlert",
  "messageIncidentAlertfit",
  "messageSystemAlert",
  "messageSystemAlertfit",
  "messageTravelAdvisory",
  "messageTravelAdvisoryfit",
  "navBottom",
  "navTop",
];

export const DotcomPlacementsProvider = (props) => {
  const {
    additionalContentParameters,
    appPlacementComponents = {},
    children,
    globalPlacementIds,
    placementsConfig,
  } = props;
  const effectiveContentParameters =
    additionalContentParameters && !isEmpty(additionalContentParameters)
      ? additionalContentParameters
      : {};
  const { appId, channelId, screenSize, site } = useContext(ApplicationContext);
  const { isLoggedIn } = useContext(AuthContext);
  const { locale } = useContext(LocaleContext);
  const { pageId } = usePageId();
  const { getCorporateInfo, getCustomerInfo } = useUserInfo();
  const { isCorporatePersona, setPersona } = usePersonas();
  const placementsBlocked = useAppSetting("placementsBlocked", false);
  const placementsHrefBasePath = useAppSetting("placementsHrefBasePath");
  const defaultPlacements = getBootstrapData("bootstrap-content/en/content");
  const geolocation = getBootstrapData("air/stations-data") && useGeolocation();
  const placementParameters = getPlacementParameters();
  const userLoggedIn = isLoggedIn();

  useEffect(() => {
    if (placementParameters.persona) {
      setPersona?.(placementParameters.persona);
    }
  }, []);

  return (
    <PlacementsVisibilityProvider>
      <PlacementsProvider {...getPlacementProviderProps()}>{children}</PlacementsProvider>
    </PlacementsVisibilityProvider>
  );

  function getPlacementParameters() {
    const placementParameterEntries =
      effectiveContentParameters?.placementParameters
        ?.split(";")
        .filter(Boolean)
        .map(
          (placementParameter) => placementParameter.includes(":") && placementParameter.split(":")
        ) ?? [];

    return Object.fromEntries(placementParameterEntries);
  }

  function getPlacementProviderProps() {
    const loggedInParams = userLoggedIn
      ? {
          customerInfo: getCustomerInfo(),
          ...(isCorporatePersona && getCorporateUserPlacementPayload()),
        }
      : {};

    return {
      additionalContentParameters: {
        loginState: userLoggedIn?.toString(),
        nearestStation: geolocation?.nearestStation,
        userLoggedIn,
        ...loggedInParams,
      },
      appId,
      channelId,
      defaultOnly: placementsBlocked,
      defaultPlacements,
      globalPlacementIds: globalPlacementIds.concat(DEFAULT_GLOBAL_PLACEMENT_IDS),
      hrefBasePath: placementsHrefBasePath,
      lang: locale,
      pageId,
      placementComponents: {
        ...placementComponents,
        ...appPlacementComponents,
      },
      placementsConfig,
      screenSize,
      site,
      updateOnPageLoad: false,
      ...placementParameters,
    };
  }

  function getCorporateUserPlacementPayload() {
    const { approvedCars, carOptionAvailable, hotelOptionAvailable, role } =
      getCorporateInfo() ?? {};

    return {
      corporateCarEnabled: carOptionAvailable,
      corporateCarVendors: approvedCars,
      corporateHotelEnabled: hotelOptionAvailable,
      corporateLoginState: "true",
      corporateRole: role,
    };
  }
};

DotcomPlacementsProvider.displayName = "DotcomPlacementsProvider";
DotcomPlacementsProvider.propTypes = {
  /** Additional content parameters to be passed. */
  additionalContentParameters: PropTypes.object,

  /** Application supplied object mapping to custom placement components. */
  appPlacementComponents: PropTypes.object,

  /** Content to be rendered on the page. */
  children: PropTypes.node.isRequired,

  /** Global placementIds that should be persisted between placement calls. */
  globalPlacementIds: PropTypes.arrayOf(PropTypes.string),

  /** Configuration settings used when rendering placements. */
  placementsConfig: PropTypes.shape({
    configuredCommands: PropTypes.object,
  }),
};

DotcomPlacementsProvider.defaultProps = {
  globalPlacementIds: [],
};
